import { useMatch } from "@reach/router"
import { navigate } from "gatsby-link"

import {
    GUIDE_TECH_URL,
    QUERY_STRING_PROPS,
    RESOURCES_URL,
} from "~config/constants"
import {
    BookingInternalSource,
    BookingInternalSourceEntityType,
} from "~graphql/generated/graphql"
import { getCurrentPath, getCurrentPathWithQueryParams } from "~utils/helpers"

import {
    getInternalSourceEntityTypeParam,
    getInternalSourceParam,
    getUrlQueryParam,
} from "./url-param-helpers"
import { getPathForBlog } from "./blog-helpers"

export const NAVIGATION_URLS = {
    INTERNAL: {
        HOME: "/",
        LISTING: "/listing",
        OUTFITTERS_BY_STATE: "/outfitters",
        TRIPS: "/trips",
        WISHLISTS: "/wishlists",
        HELP: "/help",
        INBOX: "/inbox",
        ACCOUNT: "/account",
        BOOKING: "/booking",
        SEARCH: "/search",
        ACCOUNT_PAYMENT_METHODS: "/account/payment-methods",
        ACCOUNT_PAYMENT_HISTORY: "/account/payment-history",
        ACCOUNT_SETTINGS: "/account/settings",
        ACCOUNT_CHANGE_PASSWORD: "/account/change-password",
        PAYMENT_REQUEST: "/payment-request",
        GROUP_PAY_MEMBER: "/group-pay-member",
        // Generated by gatsby-plugin-advanced-sitemap.
        SITEMAP: "/sitemap.xml",
    },
    EXTERNAL: {
        GUIDE_TECH: GUIDE_TECH_URL,
        ADMIN_DASHBOARD: process.env.GATSBY_DASHBOARD_URL!,
        GUIDE_TECH_URL: GUIDE_TECH_URL,
        GOOGLE_MAPS: "https://www.google.com/maps",
    },
}

export function getPaymentRequestRoute(paymentRequestId: string) {
    return `${NAVIGATION_URLS.INTERNAL.PAYMENT_REQUEST}/${paymentRequestId}`
}

export function getEmailUrl(email: string) {
    return `mailto:${email}`
}

export function getTelephoneUrl(phone: string) {
    return `tel:${phone}`
}

export function getGoogleMapsDirectionsUrl(address: string) {
    return `${NAVIGATION_URLS.EXTERNAL.GOOGLE_MAPS}/search/?api=1&query=${address}`
}

export function navigateToGuidetechPage() {
    return navigate(GUIDE_TECH_URL)
}

export function navigateToTripsPage() {
    void navigate(NAVIGATION_URLS.INTERNAL.TRIPS)
}

export function navigateToAccountPage() {
    void navigate(NAVIGATION_URLS.INTERNAL.ACCOUNT)
}

export function navigateToWishlistsPage() {
    void navigate(NAVIGATION_URLS.INTERNAL.WISHLISTS)
}

export function navigateToHelpPage() {
    void navigate(NAVIGATION_URLS.INTERNAL.HELP)
}

export function getKnowledgeBaseUrl() {
    return `${RESOURCES_URL}sportsman-success`
}

export function navigateToInboxPage() {
    void navigate(NAVIGATION_URLS.INTERNAL.INBOX)
}

export function navigateToSearchPage(urlSearchParams: string) {
    void navigate(`${NAVIGATION_URLS.INTERNAL.SEARCH}?${urlSearchParams}`)
}

export function navigateToCheckoutPage({
    listingId,
    pricingPackageId,
    quoteId,
    quotePackageId,
    step,
}: CheckoutNavigationArgs) {
    const route = getCheckoutRoute({
        listingId,
        pricingPackageId,
        quoteId,
        quotePackageId,
        step,
    })

    void navigate(route)
}

export function navigateToBookingPage(bookingId: string) {
    const route = getBookingRoute(bookingId)

    void navigate(route)
}

export function navigateToUpdatePaymentMethodForBookingModal({
    bookingId,
    paymentId,
}: {
    bookingId: string
    paymentId: string
}) {
    const bookingRoute = getBookingRoute(bookingId)
    const paymentRoute = `${bookingRoute}?${QUERY_STRING_PROPS.PENDING_PAYMENT_ID}=${paymentId}`

    void navigate(paymentRoute)
}

export function getListingRoute(id?: string) {
    const baseRoute = `${NAVIGATION_URLS.INTERNAL.LISTING}/${id}`
    const backRouteParam = getDefaultBackRouteParam()

    return `${baseRoute}?${backRouteParam}`
}

export function getOutfitterRoute(slug: string) {
    return `/${slug}`
}

export function useIsAccountRoute() {
    return useMatch(`${NAVIGATION_URLS.INTERNAL.ACCOUNT}/*`)
}

export function navigateBackOrHome() {
    void navigate(window.history.state?.back ?? NAVIGATION_URLS.INTERNAL.HOME)
}

export function navigateToWishlist(wishlistId: string) {
    void navigate(getWishlistRoute(wishlistId))
}

export function navigateToListing(listingId: string) {
    void navigate(getListingRoute(listingId))
}

function getCheckoutRoute({
    listingId,
    pricingPackageId,
    quoteId,
    quotePackageId,
    step,
}: CheckoutNavigationArgs) {
    const baseRoute = `${NAVIGATION_URLS.INTERNAL.LISTING}/${listingId}/checkout`
    const pricingPackageParam = getSearchParam(
        QUERY_STRING_PROPS.PACKAGE_ID,
        pricingPackageId
    )
    const quoteParam = getSearchParam(QUERY_STRING_PROPS.QUOTE_ID, quoteId)
    const quotePackageParam = getSearchParam(
        QUERY_STRING_PROPS.QUOTE_PACKAGE_ID,
        quotePackageId
    )
    const stepParam = getSearchParam(QUERY_STRING_PROPS.STEP, step)

    return getRouteWithInternalSourceQueryStringParams({
        baseUrl: baseRoute,
        initialParams: [
            stepParam,
            pricingPackageParam,
            quoteParam,
            quotePackageParam,
        ],
    })
}

function getWishlistRoute(wishlistId: string) {
    return `${NAVIGATION_URLS.INTERNAL.WISHLISTS}/${wishlistId}`
}

export function getSearchRouteWithQueryTerm(queryTerm: string) {
    return `${NAVIGATION_URLS.INTERNAL.SEARCH}?query=${queryTerm}`
}

export function getSearchRouteWithState(state: string) {
    return `${NAVIGATION_URLS.INTERNAL.SEARCH}?state=${state}`
}

export function getBookingRoute(bookingId: string) {
    return `${NAVIGATION_URLS.INTERNAL.BOOKING}/${bookingId}`
}

function getRouteWithInternalSourceQueryStringParams({
    baseUrl,
    initialParams = [],
}: {
    baseUrl: string
    initialParams?: string[]
}) {
    const internalSourceAndUtmParams = getInternalSourceAndUtmParams()
    const params = [...initialParams, ...internalSourceAndUtmParams]
        .filter(Boolean)
        .join("&")
    const query = params ? `?${params}` : ""
    return `${baseUrl}${query}`
}

export function navigateToActivityModal(activityId: string) {
    const currentPath = getCurrentPath()

    const activityUrl = getRouteWithInternalSourceQueryStringParams({
        baseUrl: currentPath,
        initialParams: [`${QUERY_STRING_PROPS.ACTIVITY_ID}=${activityId}`],
    })

    return navigate(activityUrl)
}

export function navigateToBlog(isOutfitterBlog: boolean) {
    const path = getPathForBlog(isOutfitterBlog)

    return navigate(path)
}

export function isBookingInvitation() {
    const internalSource = getInternalSourceParam()
    const internalSourceEntityType = getInternalSourceEntityTypeParam()

    // SendQuote here refers to sending BookingInvites
    // This check in intentionally redundant. Anything with an internal source of SendQuote or MarketplaceSendQuote will also have and entity type of BookingInvites
    // The redundancy is to future proof us a bit in case we add more booking invite internal sources
    return (
        internalSource === BookingInternalSource.SendQuote ||
        internalSource === BookingInternalSource.SendQuoteMarketplace ||
        internalSourceEntityType ===
            BookingInternalSourceEntityType.BookingInvites
    )
}

function getInternalSourceAndUtmParams() {
    const paramName = [
        QUERY_STRING_PROPS.INTERNAL_SOURCE,
        QUERY_STRING_PROPS.INTERNAL_SOURCE_ENTITY_ID,
        QUERY_STRING_PROPS.INTERNAL_SOURCE_ENTITY_TYPE,
        QUERY_STRING_PROPS.UTM_CAMPAIGN,
        QUERY_STRING_PROPS.UTM_CONTENT,
        QUERY_STRING_PROPS.UTM_MEDIUM,
        QUERY_STRING_PROPS.UTM_SOURCE,
        QUERY_STRING_PROPS.UTM_TERM,
    ]

    return paramName.map(getSearchParamFromUrl).filter(Boolean)
}

function getSearchParam(name: string, value?: string | null) {
    if (!value) return ""

    return `${name}=${value}`
}

function getSearchParamFromUrl(name: string) {
    return getSearchParam(name, getUrlQueryParam(name))
}

// Adding the default back route param to a route when navigating will cause
// the BackButton on that page to go back to the current page with all search params intact.
// Unless a custom onClick is passed to the BackButton (which will override the default behavior),
function getDefaultBackRouteParam() {
    const backRoute = getCurrentPathWithQueryParams()

    return `${QUERY_STRING_PROPS.BACK_ROUTE}=${backRoute}`
}

export function getManageListingPageUrl(listingId: string) {
    return `${NAVIGATION_URLS.EXTERNAL.ADMIN_DASHBOARD}manage-listings/${listingId}`
}

export function navigateToVerifyPaymentMethod({
    bookingId,
    paymentId,
}: {
    bookingId: string
    paymentId: string
}) {
    void navigate(getVerifyPaymentMethodRoute({ bookingId, paymentId }))
}

export function getVerifyPaymentMethodRoute({
    bookingId,
    paymentId,
}: {
    bookingId: string
    paymentId: string
}) {
    return `${NAVIGATION_URLS.INTERNAL.BOOKING}/${bookingId}?${QUERY_STRING_PROPS.VERIFY_PAYMENT_METHOD}=${paymentId}`
}

interface CheckoutNavigationArgs {
    listingId: string
    pricingPackageId?: string | null
    quoteId?: string | null
    quotePackageId?: string | null
    step?: string | null
}
